import firebase from '../config/firebase';
import emailService from './email.service';
import emailServiceWithAttachments from './sendEmailWithAttachments.service';
import userService from './user.service';
import freightFinancialService from './freight-financial.service';
import {
    validateRequiredFreightDocumentsForBoletoSend,
    buildFreightInvoicePendingDocumentsMessage,
    buildFreightBoletoEmailAttachmentRefs,
} from '../util/freight/freight-document-utils';
import {
    formatFinancialStatusValue,
} from '../util/freight/freight-v2-table-utils';
import {
    buildFinancialInstallmentsForFreight,
    mergeInstallmentUpdate,
} from '../util/freight/freight-financial-installments';
import { getShipperDocCached } from '../util/shipper/shipperDocCache';
require('firebase/auth')

const db = firebase.firestore();

function extractShipperFinancialContacts(shipperData) {
    const contacts = Array.isArray(shipperData?.contact) ? shipperData.contact : [];

    return contacts
        .filter((contact) => Boolean(contact?.receivesFinancial))
        .map((contact) => ({
            name: String(contact?.nome || contact?.name || "").trim(),
            email: String(contact?.email || "").trim(),
        }))
        .filter((contact) => contact.email);
}

function formatFreightRouteLabel(freightData) {
    const originCity = freightData?.firstDelivery?.city || "";
    const originUf = freightData?.firstDelivery?.uf || "";
    const destinationCity = freightData?.lastDelivery?.city || "";
    const destinationUf = freightData?.lastDelivery?.uf || "";

    return `${originCity}${originUf ? ` - ${originUf}` : ""} para ${destinationCity}${
        destinationUf ? ` - ${destinationUf}` : ""
    }`.trim();
}

function buildFreightInvoiceEmailShell({ title, bodyHtml }) {
    return `
        <!DOCTYPE html>
        <html lang="pt-BR">
        <head>
            <meta charset="UTF-8" />
            <title>${title}</title>
            <style>
                body {
                    font-family: Arial, sans-serif;
                    background-color: #f6f6f6;
                    padding: 20px;
                    margin: 0;
                    color: #000;
                }
                .container {
                    margin: auto;
                    background-color: #002444;
                    border-radius: 8px;
                    padding: 30px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                    color: #ffffff;
                }
                .logo {
                    text-align: center;
                    margin-bottom: 20px;
                }
                .logo img {
                    width: 200px;
                }
                .bodyWork {
                    text-align: center;
                    margin: 20px 0;
                }
                h1 {
                    font-size: 22px;
                    border-bottom: 1px solid #e0e0e0;
                    padding-bottom: 10px;
                }
                p {
                    font-size: 16px;
                    line-height: 1.6;
                }
                .credenciais {
                    text-align: start;
                    display: inline-block;
                }
                .footer {
                    margin-top: 30px;
                    font-size: 13px;
                    text-align: center;
                    background-color: #002444;
                    border-radius: 8px;
                    padding: 10px;
                    color: #fff;
                }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="logo">
                    <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2FFortio%20H%201.png?alt=media&token=0bdbbdbf-3696-4484-ae5f-ad63150e68b7" alt="Fortio Logo" />
                </div>
            </div>
            <div class="bodyWork">
                <h1>${title}</h1>
                <div class="credenciais">
                    ${bodyHtml}
                </div>
            </div>
            <div class="footer">
                <p><strong>Fortio - Plataforma de Fretes</strong></p>
            </div>
        </body>
        </html>
    `;
}

async function sendEmailToFinancialContacts(contacts, emailPayload) {
    const results = await Promise.all(
        contacts.map((contact) =>
            emailService.sendEmail({
                ...emailPayload,
                to: contact.email,
            }),
        ),
    );

    const failedContacts = results
        .map((result, index) => ({ result, contact: contacts[index] }))
        .filter(({ result }) => !result?.success);

    if (failedContacts.length > 0) {
        const failedEmails = failedContacts
            .map(({ contact }) => contact.email)
            .join(", ");
        return {
            sent: false,
            reason: `Falha ao enviar e-mail para: ${failedEmails}.`,
        };
    }

    return { sent: true, data: results.map((result) => result.data) };
}

async function sendEmailWithAttachmentsToFinancialContacts(
    contacts,
    emailPayload,
) {
    const results = await Promise.all(
        contacts.map((contact) =>
            emailServiceWithAttachments.sendEmail({
                ...emailPayload,
                to: contact.email,
            }),
        ),
    );

    const failedContacts = results
        .map((result, index) => ({ result, contact: contacts[index] }))
        .filter(({ result }) => !result?.success);

    if (failedContacts.length > 0) {
        const failedEmails = failedContacts
            .map(({ contact }) => contact.email)
            .join(", ");
        const firstError = failedContacts[0]?.result?.error;
        const errorDetail =
            firstError?.message ||
            firstError?.details?.message ||
            firstError?.code ||
            "";

        return {
            sent: false,
            reason: errorDetail
                ? `Falha ao enviar e-mail para: ${failedEmails}. ${errorDetail}`
                : `Falha ao enviar e-mail para: ${failedEmails}.`,
        };
    }

    return { sent: true, data: results.map((result) => result.data) };
}

async function updateFreightFinancialStatusToIssued(freightId, record = {}) {
    const installmentNumber = Number(record?.installmentNumber) || 1;
    const financialInstallments = Array.isArray(record?.financialInstallments)
        ? record.financialInstallments
        : buildFinancialInstallmentsForFreight(record);
    const issuedStatus = "emitido";

    const payload = {
        statusFinancial: issuedStatus,
        financialStatus: formatFinancialStatusValue(issuedStatus),
        installmentNumber,
        installmentTotal:
            record?.installmentTotal ?? financialInstallments.length,
        dueDate: record?.dueDate ?? null,
        value: Number(
            record?.installmentValue ??
                record?.value ??
                record?.freight?.valueFreightage ??
                0,
        ),
        financialInstallments: mergeInstallmentUpdate(
            financialInstallments,
            installmentNumber,
            {
                statusFinancial: issuedStatus,
                financialStatus: formatFinancialStatusValue(issuedStatus),
            },
        ),
    };

    const result = await freightFinancialService.saveFinancialRecord(
        freightId,
        payload,
    );

    return result?.data ?? null;
}

const formatCurrency = (value) => {
    if (!value || isNaN(Number(value))) return "R$ 0,00";
    return Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    }).format(Number(value));
};

function formatChargeDueDateLabel(value) {
    if (!value) return "";

    if (typeof value === "string") {
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const [, year, month, day] = isoMatch;
            return `${day}/${month}/${year}`;
        }

        const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (brMatch) return value;
    }

    const date =
        value?.toDate?.() ||
        (Number.isFinite(value?.seconds)
            ? new Date(value.seconds * 1000)
            : value instanceof Date
              ? value
              : new Date(value));

    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

function resolveChargePvfLabel(record, freightData, freightId) {
    const pvfNumber =
        record?.numberSerial ||
        freightData?.numberSerial ||
        freightId ||
        "Não informado";

    const installmentTotal = Number(record?.installmentTotal) || 1;
    const installmentLabel = record?.installmentLabel;
    if (installmentTotal > 1 && installmentLabel) {
        return `${pvfNumber} (Parcela ${installmentLabel})`;
    }

    return String(pvfNumber);
}

const paymentService = {

    // Email do payment
    async prepareEmail(dadoDriver, dataFreight, Subject, obs, valorSolicitado, valorOriginal, valorAdicional, valorDesconto, type, user, driver) {

        const dateFormat = (date) => {
            if (!date) return "";

            const [year, month, day] = date.split('-');
            return `${day}/${month}/${year}`;
        };

        const freightNumber = dataFreight?.numberSerial;
        const subject = `${driver === "terceiros" ? "Terceiros - " : ""}${Subject} - Carga ${dateFormat(dataFreight?.firstDelivery.dateColeta) || ""} - ${dataFreight?.clientPayment?.name} - ${dataFreight?.firstDelivery?.city || ""} - ${dataFreight?.firstDelivery?.uf || ""} para ${dataFreight?.lastDelivery?.city || ""} - ${dataFreight?.lastDelivery?.uf || ""} - ${dataFreight?.numberSerial || ""}`;
        const assunto = `${Subject} - Carga ${dateFormat(dataFreight?.firstDelivery.dateColeta) || ""} - ${dataFreight?.clientPayment?.name} <br> ${dataFreight?.firstDelivery?.city || ""} - ${dataFreight?.firstDelivery?.uf || ""} para ${dataFreight?.lastDelivery?.city || ""} - ${dataFreight?.lastDelivery?.uf || ""} - ${dataFreight?.numberSerial || ""}`;
        const to = await userService.getPaymentEmailByShipperId(user?.uidShipper);

        if (!to) {
            console.warn(
                "E-mail de pagamento não configurado no cadastro do embarcador do usuário logado."
            );
            return {
                sent: false,
                reason:
                    "E-mail de pagamento não configurado no cadastro do embarcador do usuário logado.",
            };
        }

        const getBankData = () => {
            const bank = (dadoDriver.documents && dadoDriver.documents[0] && dadoDriver.documents[0].dataBank) || {};
            return {
                bankName: bank.bankName || "Banco não informado",
                agency: bank.agency || "Agência não informada",
                account: `${bank.accountNumber || "Conta não informada"} - ${bank.accountDigit || "Dígito não informado"}`,
                pix: bank.pix || "Pix não informado",
                holderName: bank.holderAccount || "Titular não informado",
            };
        };

        const bank = getBankData();

            const html = `
                <!DOCTYPE html>
                <html lang="pt-BR">
                <head>
                <meta charset="UTF-8" />
                <title>${subject}</title>
                <style>
                body {
                    font-family: Arial, sans-serif;
                    background-color: #f6f6f6;
                    padding: 20px;
                    margin: 0;
                    color: #000;
                }
                .container {
                    margin: auto;
                    background-color: #002444;
                    border-radius: 8px;
                    padding: 30px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                    color: #ffffff;
                }
                .logo {
                    text-align: center;
                    margin-bottom: 20px;
                }
                .logo img {
                    width: 200px;
                }
                .bodyWork {
                    text-align: center;
                    justify-content: center;
                    align-items: center;
                    margin: 20px 0;
                }
                h1 {
                    font-size: 22px;
                    border-bottom: 1px solid #e0e0e0;
                    padding-bottom: 10px;
                }
                p {
                    font-size: 16px;
                    line-height: 1.6;
                }
                .pValorSolicitado {
                    font-size: 18px;
                    line-height: 1.6;
                }
                .img-row {
                    text-align: center;
                    margin-top: 10px;
                }
                .labelImg {
                    text-align: center;
                    margin-top: 5px;
                }
                .credenciais {
                    text-align: start;
                    display: inline-block;
                }
                .labelImg p, .img-row a {
                    font-size: 14px;
                    display: inline-block;
                    margin: 0 12px;
                }
                .footer {
                    margin-top: 30px;
                    font-size: 13px;
                    text-align: center;
                    justify-content: center;
                    align-items: center;
                    background-color: #002444;
                    border-radius: 8px;
                    padding: 10px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                    color: #fff;
                }
                </style>
                </head>
                <body>
                    <div class="container">
                    <div class="logo">
                        <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2FFortio%20H%201.png?alt=media&token=0bdbbdbf-3696-4484-ae5f-ad63150e68b7" alt="Fortio Logo" />
                    </div>
                    </div>

                    <div class="bodyWork">
                        <h1>${(driver === "terceiros" ? "Terceiros - " : "") + assunto}</h1>

                        <div class="credenciais">
                            <p><strong>Embarcador:</strong> ${dataFreight?.clientPayment?.name || "Não informado"}</p>
                            ${driver === "terceiros" ? "" : `<p><strong>Motorista:</strong> ${dadoDriver?.name || "Não informado"}</p>`}
                            ${driver === "terceiros" ? "" : `<p><strong>Titular da Conta:</strong> ${bank?.holderName || "Não informado"}</p>`}
                            ${driver === "terceiros" ? "" : `<p><strong>Dados Bancários:</strong> Banco: ${bank?.bankName || "Não informado"} / AG: ${bank?.agency || "Não informado"} / Conta: ${bank?.account || "Não informado"}</p>`}
                            ${driver === "terceiros" ? "" : `<p><strong>Chave PIX:</strong> ${bank?.pix || "Não informado"}</p>`}
                            <p><strong>Frete:</strong> ${dataFreight?.numberSerial || "-"} - ${dataFreight?.firstDelivery?.city || ""} - ${dataFreight?.firstDelivery?.uf || ""} para ${dataFreight?.lastDelivery?.city || ""} - ${dataFreight?.lastDelivery?.uf || ""}</p>
                            <p><strong>Tipo do Pagamento:</strong> ${type}</p>
                            ${driver === "terceiros" ? "" : `<p><strong>Valor Total Negociado:</strong> ${formatCurrency(dataFreight?.driver?.valueNegotiated)}</p>`}
                            <p><strong>Valor:</strong> ${formatCurrency(valorOriginal)}</p>
                            <p><strong>Valor Adicional:</strong> ${formatCurrency(valorAdicional)} </p>
                            <p><strong>Valor Desconto:</strong> ${formatCurrency(valorDesconto)} </p>
                            <p class="pValorSolicitado"><strong>Valor Solicitado:</strong> <strong>${formatCurrency(valorSolicitado)}</strong></p>
                            ${driver === "terceiros" ? "" : `<p><strong>Negociação:</strong> ${dataFreight?.driver?.paymentCondition || "Não informado"}</p>`}
                            <p><strong>Observações:</strong> ${obs || "Nenhuma observação"}</p>
                            <p><strong>Solicitante:</strong> ${user?.name || "Não informado"}</p>
                        </div>
                    </div>

                    <div class="footer">
                        <p><strong>Siga-nos:</strong></p>
                        <div class="img-row">
                            <img style="margin-right: 20px;" src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Ffacebook.png?alt=media&token=a3d67757-8280-40d4-ab36-dd9623d6c910" alt="Facebook" width="40" height="40" />
                            <img style="margin-right: 20px;" src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Finstagram.png?alt=media&token=3096f43f-0d40-42b3-a091-78bf1c7722cd" alt="Instagram" width="40" height="40" />
                            <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Flinkedin.png?alt=media&token=4ccbc038-fee6-4760-b55c-145feeb84f32" alt="LinkedIn" width="40" height="40" />
                        </div>
                    </div>
                </body>
            </html>
        `;

        try {
            const result = await emailService.sendEmail({ to, subject, html });

            if (result.success) {
                console.log("E-mail enviado com sucesso:", result.data);
                return { sent: true, data: result.data };
            }

            console.error("Erro ao enviar e-mail:", result.error);
            return {
                sent: false,
                reason: result.error || "Erro ao enviar e-mail de solicitação de pagamento.",
            };
        } 
        catch (err) {
            console.error("Erro inesperado ao tentar enviar e-mail:", err);
            return {
                sent: false,
                reason: err?.message || "Erro inesperado ao tentar enviar e-mail.",
            };
        }

    },

    async prepareEmailDriver(password, email) {

        const subject = "Envio de senha para acesso Fortio APP";
        const to = email;

        // const formatCurrency = (value) => {
        //     if (!value || isNaN(Number(value))) return "Não informado";
        //     return Intl.NumberFormat('pt-BR', {
        //     style: 'currency',
        //     currency: 'BRL',
        //     }).format(Number(value));
        // };

        const html = `
            <html lang="pt-BR">
            <head>
                <meta charset="UTF-8" />
                <title>${subject}</title>
                <style>
                body {
                    font-family: Arial, sans-serif;
                    background-color: #f6f6f6;
                    padding: 20px;
                    margin: 0;
                    color: #000;
                }
                .container {
                    margin: auto;
                    background-color: #002444;
                    border-radius: 8px;
                    padding: 30px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                    color: #ffffff;
                }
                .logo {
                    text-align: center;
                    margin-bottom: 20px;
                }
                .logo img {
                    width: 150px;
                }
                .bodyWork {
                    text-align: center;
                    justify-content: center;
                    align-items: center;
                    margin: 20px 0;
                }
                h1 {
                    font-size: 22px;
                    border-bottom: 1px solid #e0e0e0;
                    padding-bottom: 10px;
                }
                p {
                    font-size: 16px;
                    line-height: 1.6;
                }
                .pValorSolicitado {
                    font-size: 18px;
                    line-height: 1.6;
                }
                .img-row {
                    text-align: center;
                    margin-top: 10px;
                }
                .labelImg {
                    text-align: center;
                    margin-top: 5px;
                }
                .credenciais {
                    text-align: start;
                    display: inline-block;
                }
                .labelImg p, .img-row a {
                    font-size: 14px;
                    display: inline-block;
                    margin: 0 12px;
                }
                .footer {
                    margin-top: 30px;
                    font-size: 13px;
                    text-align: center;
                    justify-content: center;
                    align-items: center;
                    background-color: #002444;
                    border-radius: 8px;
                    padding: 10px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                    color: #fff;
                }
                </style>
            </head>
            <body>
                <div class="container">
                <div class="logo">
                    <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2FFortio%20H%201.png?alt=media&token=0bdbbdbf-3696-4484-ae5f-ad63150e68b7" alt="Fortio Logo" />
                </div>
                </div>

                <div class="bodyWork">
                    <h1><strong>Bem-vindo à Fortio!</strong></h1>

                    <p>A partir de agora você tem acesso à maior plataforma<br> de frete do Brasil!</p>

                    <p><strong>Para acessar, baixe o app e use as credenciais de primeiro<br> acesso:</strong></p>

                    <div class="credenciais">
                        <p><strong>Login:</strong> ${email || "Não informado"}</p>
                        <p><strong>Senha:</strong> ${password || "Não informado"}</p>
                    </div>

                    <p>Na Fortio você recebe notificações de fretes diretamente no app,<br>
                        gerencia suas entregas, seus ganhos e seus benefícios.<br>
                        Baixe agora mesmo!</p>

                    <p><strong>Baixe nosso app nas principais plataformas:</strong></p>
                    
                    <div class="img-row">
                        <a href="https://play.google.com/store/apps/details?id=com.fortio" target="_blank" rel="noopener noreferrer">
                            <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Fandroid.png?alt=media&token=692808af-2c9a-49f0-ac05-b62321be0ea9" alt="Android" width="40" height="40" />
                        </a>
                        <a href="https://apps.apple.com/br/app/fortio/id6748965216?l=en-GB" target="_blank" rel="noopener noreferrer">
                            <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Fapple-logo.png?alt=media&token=f9dfc01d-bb48-4041-b3f8-7dcf3d680d35" alt="iPhone" width="40" height="40" />
                        </a>
                    </div>
                    <div class="labelImg">
                        <p>Play Store</p>
                        <p>Apple Store</p>
                    </div>
                </div>

                <div class="footer">
                    <p><strong>Siga-nos:</strong></p>
                    <div class="img-row">
                        <img style="margin-right: 20px;" src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Ffacebook.png?alt=media&token=a3d67757-8280-40d4-ab36-dd9623d6c910" alt="Facebook" width="40" height="40" />
                        <img style="margin-right: 20px;" src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Finstagram.png?alt=media&token=3096f43f-0d40-42b3-a091-78bf1c7722cd" alt="Instagram" width="40" height="40" />
                        <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Flinkedin.png?alt=media&token=4ccbc038-fee6-4760-b55c-145feeb84f32" alt="LinkedIn" width="40" height="40" />
                    </div>
                </div>
            </body>
            </html>
        `;

        try {

            const result = await emailService.sendEmail({ to, subject, html });

            if (result.success) {
                console.log("E-mail enviado com sucesso:", result.data);
            } else {
                console.error("Erro ao enviar e-mail:", result.error);
            }
        } 
        catch (err) {
            console.error("Erro inesperado ao tentar enviar e-mail:", err);
        }

    },

    async prepareEmailFreight(dataFreight, Subject, numberSerial) {

        const subject = Subject + " - " + numberSerial || 'Solicitação de Envio de Frete'; 
        const to = "venancio@fortio.com.br";
        // const to = "juliogabrielti@gmail.com";

        const html = `
            <!DOCTYPE html>
            <html lang="pt-BR">
            <head>
            <meta charset="UTF-8" />
            <title>${subject}</title>
            <style>
                body {
                    font-family: Arial, sans-serif;
                    background-color: #f6f6f6;
                    padding: 20px;
                    margin: 0;
                    color: #000;
                }
                .container {
                    margin: auto;
                    background-color: #002444;
                    border-radius: 8px;
                    padding: 30px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                    color: #ffffff;
                }
                .logo {
                    text-align: center;
                    margin-bottom: 20px;
                }
                .logo img {
                    width: 200px;
                }
                .bodyWork {
                    text-align: center;
                    justify-content: center;
                    align-items: center;
                    margin: 20px 0;
                }
                h1 {
                    font-size: 22px;
                    border-bottom: 1px solid #e0e0e0;
                    padding-bottom: 10px;
                }
                p {
                    font-size: 16px;
                    line-height: 1.6;
                }
                .pValorSolicitado {
                    font-size: 18px;
                    line-height: 1.6;
                }
                .img-row {
                    text-align: center;
                    margin-top: 10px;
                }
                .labelImg {
                    text-align: center;
                    margin-top: 5px;
                }
                .credenciais {
                    text-align: start;
                    display: inline-block;
                }
                .labelImg p, .img-row a {
                    font-size: 14px;
                    display: inline-block;
                    margin: 0 12px;
                }
                .footer {
                    margin-top: 30px;
                    font-size: 13px;
                    text-align: center;
                    justify-content: center;
                    align-items: center;
                    background-color: #002444;
                    border-radius: 8px;
                    padding: 10px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                    color: #fff;
                }
            </style>
            </head>
            <body>
                <div class="container">
                    <div class="logo">
                        <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2FFortio%20H%201.png?alt=media&token=0bdbbdbf-3696-4484-ae5f-ad63150e68b7" alt="Fortio Logo" />
                    </div>
                </div>

                <div class="bodyWork">
                    <h1>${subject}</h1>

                    <div class="credenciais" >
                        <p><strong>Numero de Frete:</strong> ${dataFreight?.numberSerial || numberSerial || "Não informado"}</p>
                        <p><strong>Origem/Destino do Frete:</strong> ${dataFreight?.firstDelivery?.city || ""} - ${dataFreight?.firstDelivery?.uf || ""} para ${dataFreight?.lastDelivery?.city || ""} - ${dataFreight?.lastDelivery?.uf || ""}</p>
                        <p><strong>Tipo de Veiculo do Frete:</strong> ${dataFreight?.vehicle?.typeVehicle?.dados?.filter(v => v.selected).map(v => v.name).join(', ') || "Não informado"}</p>
                        <p><strong>Tipo de Carroceria do Frete:</strong> ${dataFreight?.vehicle?.typeBodywork?.dados?.filter(c => c.selected).map(c => c.name).join(', ') || "Não informado"}</p>
                        <p><strong>Peso do Frete:</strong> ${dataFreight?.freight?.weightCargo} </p>
                        <p><strong>Valor NotaFiscal do Frete:</strong> ${formatCurrency(dataFreight?.freight?.valueNF)} </p>
                        <p><strong>Valor do Frete:</strong> ${formatCurrency(dataFreight?.freight?.valueFreightage)} </p>
                        <p><strong>Observações do Frete:</strong> ${dataFreight?.freight?.observation} </p>
                    </div>
                </div>

                <div class="footer">
                    <p><strong>Siga-nos:</strong></p>
                    <div class="img-row">
                        <img style="margin-right: 20px;" src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Ffacebook.png?alt=media&token=a3d67757-8280-40d4-ab36-dd9623d6c910" alt="Facebook" width="40" height="40" />
                        <img style="margin-right: 20px;" src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Finstagram.png?alt=media&token=3096f43f-0d40-42b3-a091-78bf1c7722cd" alt="Instagram" width="40" height="40" />
                        <img src="https://firebasestorage.googleapis.com/v0/b/app-fortio.appspot.com/o/LogoEmail%2Flinkedin.png?alt=media&token=4ccbc038-fee6-4760-b55c-145feeb84f32" alt="LinkedIn" width="40" height="40" />
                    </div>
                </div>
            </body>
            </html>
        `;

        try {

            const result = await emailService.sendEmail({ to, subject, html });

            if (result.success) {
                console.log("E-mail enviado com sucesso:", result.data);
            } else {
                console.error("Erro ao enviar e-mail:", result.error);
            }
        } catch (err) {
            console.error("Erro inesperado ao tentar enviar e-mail:", err);
        }

    },

    async getEspecifico(idFreight) {
        console.log("[get] Buscando dados do pagamento mais especificos...");

        try {
            const snapshot = await db.collection('payment').where("idFreight", "==", idFreight).get();
            const payments = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
            }));

            return payments;
        } catch (error) {
            console.error("[get] Erro ao buscar pagamentos:", error);
            return [];
        }
    },

    async getAll() {
        console.log("[get] Buscando dados de todos os pagamentos...");

        try {
            const snapshot = await db.collection('payment').get();
            const payments = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
            }));

            return payments;
        } catch (error) {
            console.error("[get] Erro ao buscar pagamentos:", error);
            return [];
        }
    },

    async save(data){
        console.log("[save] Salvando dados do pagamento ao incluir um motorista ")

        try {
            const docRef = await db.collection('payment').add(data);
            return docRef.id;
        } catch (error) {
            console.log(error);
            throw error;
        }
    },

    async update(data){
        console.log("[update] Atualizando pagamento")

        try {
            if (!data?.id) {
                throw new Error("ID do pagamento não informado para atualizar.");
            }
            await db.collection('payment').doc(data.id).update(data);
            console.log(`Documento com id ${data.id} atualizado.`);
        } catch (error) {
            console.log("Erro ao atualizar documento:", error);
            throw error;
        }
    },

    async delete(data) {
        try {
            console.log("[delete] Deletando pagamento");

            if (!data?.id) {
                console.error("ID do pagamento não informado para deletar.");
                return;
            }

            await db.collection("payment").doc(data.id).delete();

            console.log(`Documentos com idFreight ${data.idFreight} e type ${data.type} deletados com sucesso.`);
        } catch (error) {
            console.error("Erro ao deletar pagamento:", error);
        }
    },

    async deleteAll(id) {
        try {
            console.log("[delete] Deletando pagamento");
            console.log(id);

            const querySnapshot = await db.collection('payment')
                .where("idFreight", "==", id)
                .get();

            if (querySnapshot.empty) {
                console.log("Nenhum documento encontrado para deletar.");
                return;
            }

            // Filtra os documentos que NÃO têm o campo addExpenses
            const deletePromises = querySnapshot.docs
                .filter(doc => !doc.data().addExpenses)
                .map(doc => doc.ref.delete());

            await Promise.all(deletePromises);

            console.log(`Documentos com idFreight ${id} deletados com sucesso (exceto os com addExpenses).`);
        } catch (error) {
            console.error("Erro ao deletar pagamento:", error);
        }
    },

    async deleteDetalhes(data) {
        try {
            console.log("[delete] Deletando pagamento");

            const querySnapshot = await db.collection('payment')
            .where("idFreight", "==", data.idFreight)
            .get();

            if (querySnapshot.empty) {
                console.log("Nenhum documento encontrado para deletar.");
                return;
            }

            const deletePromises = querySnapshot.docs.map((doc) => doc.ref.delete());

            await Promise.all(deletePromises);

            console.log(`Documentos com idFreight ${data.idFreight} e type ${data.type} deletados com sucesso.`);
        } catch (error) {
            console.error("Erro ao deletar pagamento:", error);
        }
    },

    async searchShipper(id) {
        try {
            const shipperId = String(id || "").trim();
            if (!shipperId) return null;

            return await getShipperDocCached(shipperId, async () => {
                console.log("[searchShipper] Buscando dados do shipper...");
                const snapshot = await db.collection("shipper").doc(shipperId).get();
                return snapshot.exists ? snapshot.data() : null;
            });
        } catch (error) {
            console.error("[searchShipper] Erro ao buscar shipper:", error);
            return null;
        }
    },

    validateFreightInvoiceBoletoSend(freightData, boletoAttachment) {
        return validateRequiredFreightDocumentsForBoletoSend(
            freightData,
            boletoAttachment,
        );
    },

    buildFreightInvoicePendingDocumentsMessage(validation) {
        return buildFreightInvoicePendingDocumentsMessage(validation);
    },

    getShipperFinancialContacts(shipperData) {
        return extractShipperFinancialContacts(shipperData);
    },

    async sendFreightControlBoletoEmail({
        freightId,
        freightData,
        record,
        shipperData,
        user,
        boletoAttachment,
    }) {
        if (!freightId) {
            return {
                success: false,
                message: "Frete não informado para envio do boleto.",
            };
        }

        const validation = validateRequiredFreightDocumentsForBoletoSend(
            freightData,
            boletoAttachment,
        );

        if (!validation.valid) {
            return {
                success: false,
                message: buildFreightInvoicePendingDocumentsMessage(validation),
                validation,
            };
        }

        const contacts = extractShipperFinancialContacts(shipperData);
        if (!contacts.length) {
            return {
                success: false,
                message:
                    'Nenhum contato do embarcador possui o checkbox "Recebe Financeiro" ativo com e-mail cadastrado.',
            };
        }

        const freightNumber =
            record?.numberSerial ||
            freightData?.numberSerial ||
            freightId;
        const routeLabel = formatFreightRouteLabel(freightData || record);
        const attachmentRefs = buildFreightBoletoEmailAttachmentRefs(
            freightData,
            boletoAttachment,
        );

        if (!attachmentRefs.length) {
            return {
                success: false,
                message:
                    "Nenhum arquivo disponível para anexar ao e-mail de boleto.",
            };
        }

        const subject = `Envio de Boleto - Frete ${freightNumber}`;
        const bodyHtml = `
            <p>Prezado(a),</p>
            <p>Segue em anexo o boleto e os documentos referentes ao frete <strong>${freightNumber}</strong>.</p>
            <p><strong>Rota:</strong> ${routeLabel || "Não informada"}</p>
            <p><strong>Embarcador:</strong> ${record?.shipper?.name || freightData?.shipper?.name || "Não informado"}</p>
            <p><strong>Valor da fatura:</strong> ${formatCurrency(
                record?.freight?.valueFreightage ??
                    record?.installmentValue ??
                    record?.value ??
                    freightData?.freight?.valueFreightage,
            )}</p>
            <p>Os arquivos seguem anexados a este e-mail, com o boleto como primeiro anexo.</p>
            <p><strong>Enviado por:</strong> ${user?.name || "Não informado"}</p>
        `;
        const html = buildFreightInvoiceEmailShell({
            title: subject,
            bodyHtml,
        });

        const emailResult = await sendEmailWithAttachmentsToFinancialContacts(
            contacts,
            {
                subject,
                html,
                attachmentRefs,
                attachments: [],
            },
        );

        if (!emailResult.sent) {
            return {
                success: false,
                message:
                    emailResult.reason ||
                    "Não foi possível enviar o e-mail com o boleto.",
            };
        }

        try {
            const updatedFinancial = await updateFreightFinancialStatusToIssued(
                freightId,
                {
                    ...freightData,
                    ...record,
                    boletoAttachment,
                },
            );

            return {
                success: true,
                message:
                    "Boleto enviado com sucesso. Status da fatura atualizado para Emitido.",
                data: updatedFinancial,
                recipients: contacts.map((contact) => contact.email),
            };
        } catch (error) {
            console.error(
                "Erro ao atualizar status financeiro após envio do boleto:",
                error,
            );
            return {
                success: false,
                message:
                    error?.message ||
                    "O boleto foi enviado, mas não foi possível atualizar o status da fatura.",
            };
        }
    },

    async sendFreightControlChargeEmail({
        freightId,
        freightData,
        record,
        shipperData,
        user,
    }) {
        if (!freightId) {
            return {
                success: false,
                message: "Frete não informado para efetuar cobrança.",
            };
        }

        const contacts = extractShipperFinancialContacts(shipperData);
        if (!contacts.length) {
            return {
                success: false,
                message:
                    'Nenhum contato do embarcador possui o checkbox "Recebe Financeiro" ativo com e-mail cadastrado.',
            };
        }

        const freightNumber =
            record?.numberSerial ||
            freightData?.numberSerial ||
            freightId;
        const pvfLabel = resolveChargePvfLabel(record, freightData, freightId);
        const dueDateLabel = formatChargeDueDateLabel(record?.dueDate);
        const routeLabel = formatFreightRouteLabel(freightData || record);
        const subject = `Cobrança de Frete - ${freightNumber}`;
        const bodyHtml = `
            <p>Prezado(a),</p>
            <p>Identificamos pendência de pagamento referente ao frete <strong>${freightNumber}</strong>.</p>
            <p><strong>PVF:</strong> ${pvfLabel}</p>
            <p><strong>Data de vencimento:</strong> ${dueDateLabel || "Não informada"}</p>
            <p><strong>Rota:</strong> ${routeLabel || "Não informada"}</p>
            <p><strong>Embarcador:</strong> ${record?.shipper?.name || freightData?.shipper?.name || "Não informado"}</p>
            <p><strong>Valor da fatura:</strong> ${formatCurrency(
                record?.freight?.valueFreightage ??
                    record?.installmentValue ??
                    record?.value ??
                    freightData?.freight?.valueFreightage,
            )}</p>
            <p>Solicitamos a regularização do pagamento o quanto antes.</p>
            <p><strong>Responsável pelo envio:</strong> ${user?.name || "Não informado"}</p>
        `;
        const html = buildFreightInvoiceEmailShell({
            title: subject,
            bodyHtml,
        });

        const emailResult = await sendEmailToFinancialContacts(contacts, {
            subject,
            html,
        });

        if (!emailResult.sent) {
            return {
                success: false,
                message:
                    emailResult.reason ||
                    "Não foi possível enviar o e-mail de cobrança.",
            };
        }

        return {
            success: true,
            message: "Cobrança enviada com sucesso para os contatos financeiros.",
            recipients: contacts.map((contact) => contact.email),
        };
    },

}

export default paymentService;
